import {
  withActivitySource,
  type ActivityRequestInit,
  type ActivitySource,
} from "./activity-source";

export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly (string | number | boolean | null | undefined)[];

export type QueryParams = Record<string, QueryValue>;

export type JsonRecord = Record<string, unknown>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: string;
  readonly requestId?: string | null;
  readonly retryAfter?: string | null;
  readonly body?: unknown;

  constructor(opts: {
    status: number;
    code?: string;
    message?: string;
    detail?: string;
    requestId?: string | null;
    retryAfter?: string | null;
    body?: unknown;
  }) {
    super(opts.message || opts.detail || opts.code || "request_failed");
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code || "request_failed";
    this.detail = opts.detail;
    this.requestId = opts.requestId;
    this.retryAfter = opts.retryAfter;
    this.body = opts.body;
  }
}

export function buildQuery(params: QueryParams = {}): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null) query.append(key, String(item));
      });
      return;
    }
    if (value != null) query.set(key, String(value));
  });
  return query.toString();
}

export function withQuery(path: string, params?: QueryParams): string {
  const query = buildQuery(params);
  if (!query) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${query}`;
}

export async function parseJsonSafe<T = unknown>(res: Response): Promise<T | null> {
  if (res.status === 204 || res.status === 205) return null;
  const text = await res.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function getRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : {};
}

function stringFrom(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function messageForStatus(status: number): string {
  if (status === 0) return "network_error";
  if (status === 401) return "auth_required";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  return "request_failed";
}

export function normalizeApiError(err: unknown, fallbackCode = "request_failed"): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) {
    const shaped = err as Error & {
      status?: number;
      code?: string;
      detail?: string;
      body?: unknown;
    };
    return new ApiError({
      status: Number(shaped.status) || 0,
      code: shaped.code || fallbackCode,
      message: shaped.message,
      detail: shaped.detail,
      body: shaped.body,
    });
  }
  return new ApiError({
    status: 0,
    code: fallbackCode,
    message: typeof err === "string" ? err : "request_failed",
  });
}

export async function apiFetch(path: string, init: ActivityRequestInit = {}): Promise<Response> {
  const requestInit = withActivitySource(path, init);
  const headers = new Headers(requestInit.headers);
  headers.set("Accept", headers.get("Accept") || "application/json");
  headers.set("x-tb-client", headers.get("x-tb-client") || "web");
  if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  try {
    return await fetch(path, {
      ...requestInit,
      headers,
      credentials: init.credentials || "same-origin",
    });
  } catch (err) {
    throw new ApiError({
      status: 0,
      code: "network_error",
      message: err instanceof Error ? err.message : "network_error",
    });
  }
}

export async function jsonFromResponse<T>(
  res: Response,
  opts: { fallbackCode?: string } = {},
): Promise<T> {
  const data = await parseJsonSafe<T>(res);
  if (!res.ok) {
    const record = getRecord(data);
    const code =
      stringFrom(record.error) ||
      stringFrom(record.code) ||
      opts.fallbackCode ||
      messageForStatus(res.status);
    const detail = stringFrom(record.detail) || stringFrom(record.message);
    throw new ApiError({
      status: res.status,
      code,
      detail,
      message: detail || code,
      requestId: res.headers.get("x-request-id"),
      retryAfter: res.headers.get("retry-after"),
      body: data,
    });
  }
  return (data ?? {}) as T;
}

export async function apiJson<T>(
  path: string,
  init: ActivityRequestInit = {},
  opts: { fallbackCode?: string } = {},
): Promise<T> {
  const res = await apiFetch(path, init);
  try {
    return await jsonFromResponse<T>(res, opts);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError({
      status: 0,
      code: "network_error",
      message: err instanceof Error ? err.message : "network_error",
    });
  }
}

export function apiGet<T>(
  path: string,
  params?: QueryParams,
  opts?: { fallbackCode?: string; activitySource?: ActivitySource; signal?: AbortSignal },
): Promise<T> {
  return apiJson<T>(
    withQuery(path, params),
    { method: "GET", activitySource: opts?.activitySource, signal: opts?.signal },
    opts,
  );
}

export function apiPost<T>(
  path: string,
  body: unknown = {},
  opts?: { fallbackCode?: string; activitySource?: ActivitySource },
): Promise<T> {
  return apiJson<T>(
    path,
    {
      method: "POST",
      activitySource: opts?.activitySource,
      body: JSON.stringify(body),
    },
    opts,
  );
}

export function apiPatch<T>(
  path: string,
  body: unknown = {},
  opts?: { fallbackCode?: string; activitySource?: ActivitySource },
): Promise<T> {
  return apiJson<T>(
    path,
    {
      method: "PATCH",
      activitySource: opts?.activitySource,
      body: JSON.stringify(body),
    },
    opts,
  );
}

export function apiDelete<T>(
  path: string,
  body?: unknown,
  opts?: { fallbackCode?: string; activitySource?: ActivitySource },
): Promise<T> {
  return apiJson<T>(
    path,
    {
      method: "DELETE",
      activitySource: opts?.activitySource,
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    opts,
  );
}

export function getErrorMessage(err: unknown, fallback: string): string {
  const apiErr = normalizeApiError(err);
  return apiErr.detail || apiErr.message || fallback;
}
